
import { Package, Truck } from 'lucide-react';

/* Small pieces shared by the phone pages (/m/orders, /m/simulate). */

export const eur = (v) => (v === null || v === undefined || isNaN(v)) ? '–' : `€${Number(v).toFixed(2)}`;

export const flag = (name, iso) => {
  const code = iso || ({ Belgium: 'BE', Netherlands: 'NL', France: 'FR', Germany: 'DE', Spain: 'ES', Italy: 'IT', Portugal: 'PT', 'United Kingdom': 'GB', 'United States': 'US', Switzerland: 'CH', Austria: 'AT', Denmark: 'DK', Sweden: 'SE', Poland: 'PL', Ireland: 'IE', Luxembourg: 'LU', Norway: 'NO', Australia: 'AU', Canada: 'CA', 'United Arab Emirates': 'AE', 'Saudi Arabia': 'SA', 'Hong Kong': 'HK', Singapore: 'SG' })[name];
  if (!code) return '';
  return String.fromCodePoint(...code.split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

/* One packing solution as the engine returns it (bins, carrier, cost). */
export function Solution({ sol, title }) {
  if (!sol) return null;
  const bins = sol.bins_used || [];
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</div>
        <div className="text-lg font-semibold tabular-nums">{eur(sol.total_cost)}</div>
      </div>
      <div className="text-xs">{sol.description}</div>
      <div className="text-xs text-muted-foreground flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> {sol.carrier_name || '–'}</div>
      <ul className="text-xs space-y-1">
        {bins.map((b, i) => (
          <li key={i} className="flex items-center justify-between gap-2 border-t border-border/50 pt-1">
            <span className="truncate"><Package className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />{b.bin_name} · {(b.items || []).length || b.items_packed || 0} item{((b.items || []).length || b.items_packed) === 1 ? '' : 's'}{b.surcharge_labels?.length ? ` · ${b.surcharge_labels.join(', ')}` : ''}</span>
            <span className="tabular-nums shrink-0">{eur(b.cost ?? b.shipping_cost)}{b.surcharge_amount > 0 ? <span className="text-muted-foreground"> +{eur(b.surcharge_amount)}</span> : null}</span>
          </li>
        ))}
      </ul>
      {sol.packaging_cost > 0 && <div className="text-[11px] text-muted-foreground">shipping {eur(sol.shipping_cost)} + packaging {eur(sol.packaging_cost)}</div>}
      {sol.surcharge_total > 0 && <div className="text-[11px] text-muted-foreground">incl. surcharges {eur(sol.surcharge_total)}{sol.surcharge_labels?.length ? ` · ${sol.surcharge_labels.join(', ')}` : ''}</div>}
    </div>
  );
}

/* The engine's other-carrier block: {alternatives: [...], standard_carrier, standard_total, ...}
   (an older shape was a plain list). Our cost per carrier, and the checkout price when
   the country offers that carrier as a paid alternative. */
export function Alternatives({ alts }) {
  const list = Array.isArray(alts) ? alts : (alts?.alternatives || []);
  if (list.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-xs space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Other carriers</div>
      {alts?.standard_carrier && <div className="flex items-center justify-between gap-2 text-muted-foreground"><span className="truncate">{alts.standard_carrier} (chosen)</span><span className="tabular-nums">{eur(alts.standard_total)}</span></div>}
      {list.slice(0, 6).map((a, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <span className="truncate">{a.label || a.carrier_name || a.name || a.carrier}</span>
          <span className="tabular-nums shrink-0">{eur(a.total_cost ?? a.cost ?? a.price)}{a.checkout_price != null ? <span className="text-muted-foreground"> · checkout {eur(a.checkout_price)}</span> : null}</span>
        </div>
      ))}
    </div>
  );
}
